import { useMemo } from "react";
import { OrganisationService } from "@/services/OrganisationService";
import { useQuery } from "@tanstack/react-query";
import type { Organisation } from "@/types/Organisation";

interface UseOrganisationQueryOptions {
  enabled?: boolean;
  staleTime?: number;
}

/**
 * React Query hook that fetches a single organisation by ID.
 *
 * Used when the member record has `ownerOfOrganisationId` but the embedded
 * `organisation` object is not hydrated — for example, in the AppSidebar.
 */
export function useOrganisationQuery(
  id: string | null | undefined,
  options: UseOrganisationQueryOptions = {},
) {
  const { enabled = true, staleTime = 5 * 60 * 1000 } = options;

  const organisationService = useMemo(() => new OrganisationService(), []);

  return useQuery<Organisation | null>({
    queryKey: ["organisation", id],
    queryFn: () => organisationService.getOrganisationById(id!),
    enabled: enabled && !!id,
    staleTime,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}
